import { ApiClientError } from "@/api/client";
import { getAnalyticsContext, normalizeRouteTemplate } from "@/analytics/context";
import { trackEvent as trackGaEvent, trackPageView as trackGaPageView } from "@/analytics/ga4";
import type { AnalyticsPayload } from "@/analytics/ga4";

export type AnalyticsEventName =
  | "landing_cta_clicked"
  | "auth_mode_changed"
  | "login"
  | "sign_up"
  | "auth_failed"
  | "workspace_setup_completed"
  | "onboarding_step_completed"
  | "onboarding_completed"
  | "goal_created"
  | "project_created"
  | "task_created"
  | "task_clarification_required"
  | "clarification_submitted"
  | "task_plan_generated"
  | "task_plan_generation_failed"
  | "task_completed"
  | "task_opened"
  | "add_task_sheet_opened"
  | "task_updated"
  | "subtasks_regenerated"
  | "atomic_task_completed"
  | "blocker_resolve_requested"
  | "agent_added"
  | "agent_connection_changed"
  | "integration_toggled"
  | "notification_toggled"
  | "activity_filter_applied"
  | "task_output_viewed"
  | "task_output_shared"
  | "archive_item_restored"
  | "report_export_clicked"
  | "app_opened"
  | "workspace_switched"
  | "daily_active_ping"
  | "credits_pack_selected"
  | "credits_purchase_succeeded"
  | "credits_purchase_failed"
  | "credit_debit_recorded"
  | "low_credit_threshold_reached";

const shouldSkipTracking = (): boolean => getAnalyticsContext().user_role === "platform_admin";

const buildPayload = (payload: AnalyticsPayload): AnalyticsPayload => ({
  event_version: 1,
  ...getAnalyticsContext(),
  ...payload
});

export const trackEvent = (eventName: AnalyticsEventName, payload: AnalyticsPayload = {}): void => {
  if (shouldSkipTracking()) return;
  trackGaEvent(eventName, buildPayload(payload));
};

export const trackPageView = (input: {
  pathname: string;
  search?: string;
  title?: string;
  surface?: string;
}): void => {
  if (shouldSkipTracking()) return;
  const routeTemplate = normalizeRouteTemplate(input.pathname);
  const pagePath = `${input.pathname}${input.search || ""}`;
  const pageLocation = typeof window !== "undefined" ? `${window.location.origin}${pagePath}` : pagePath;

  trackGaPageView(
    buildPayload({
      page_path: pagePath,
      page_location: pageLocation,
      page_title: input.title || (typeof document !== "undefined" ? document.title : "Taskkar"),
      route_template: routeTemplate,
      surface: input.surface || routeTemplate
    })
  );
};

export const extractAnalyticsErrorMeta = (error: unknown): { error_kind: string; error_status?: number } => {
  if (error instanceof ApiClientError) {
    return {
      error_kind: error.kind,
      error_status: error.status
    };
  }

  if (error instanceof Error) {
    return { error_kind: "unknown" };
  }

  return { error_kind: "unknown" };
};
